/**
 * Thin footer that fetches /version on mount and surfaces both version
 * systems: the external marketing version (primary, customer-facing) and
 * the internal build ID (secondary, revealed on hover + copied to
 * clipboard on click for support correlation).
 *
 * Placement: bottom of the AppShell layout, only on authed routes.
 * The login page / legal scaffolds don't show it.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

interface MarketingInfo {
  family: string;
  product: string;
  version: string;
  codename: string;
  releaseStage: 'alpha' | 'beta' | 'rc' | 'stable';
  releaseDate: string;
}

interface VersionInfo {
  version: string;
  gitSha: string;
  deployedAt: string | null;
  marketing: MarketingInfo | null;
}

export function VersionFooter() {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.getVersion()
      .then((res) => setInfo({
        version: res.version,
        gitSha: res.gitSha,
        deployedAt: res.deployedAt,
        marketing: res.marketing,
      }))
      .catch(() => undefined);
  }, []);

  if (!info || info.version === 'unknown') return null;

  const deployedAtShort = info.deployedAt
    ? new Date(info.deployedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  // External label: "Nexus 0.9.0 beta" (stable stage hides the suffix).
  // Falls back to the internal build ID if marketing metadata is missing
  // (older Lambdas, fresh deploys where the stamp failed, etc).
  const m = info.marketing;
  const stageSuffix = m && m.releaseStage !== 'stable' ? ` ${m.releaseStage}` : '';
  const label = m
    ? `${m.product} ${m.version}${stageSuffix}`
    : info.version;

  const handleCopy = async () => {
    const parts = [label, `build ${info.version} (${info.gitSha})`];
    if (deployedAtShort) parts.push(`deployed ${deployedAtShort}`);
    const text = parts.join(' · ');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable (old browser, insecure context) */ }
  };

  const tooltip = [
    m ? `${m.product} ${m.version}${stageSuffix}${m.codename ? ` "${m.codename}"` : ''}` : null,
    `Build ${info.version} (${info.gitSha})`,
    deployedAtShort ? `Deployed ${deployedAtShort}` : null,
    'Click to copy for support',
  ].filter(Boolean).join(' · ');

  return (
    <div className="fixed bottom-2 right-3 z-10 text-[10px] font-mono text-slate-400 dark:text-slate-600 select-none">
      <button
        type="button"
        onClick={handleCopy}
        title={tooltip}
        className="hover:text-slate-500 dark:hover:text-slate-400 transition-colors cursor-pointer"
      >
        {copied ? 'copied' : label}
      </button>
    </div>
  );
}
